/**
 * The variant matrix — PRD 02 §3, §4, §12.
 *
 * A primitive's axes describe a *matrix* of possible instances, and the matrix is **never
 * materialized**. Points are generated lazily on demand and cached, so the Studio's explorer
 * is mostly empty on purpose: an empty cell is an invitation ("tap to make"), never a gap.
 * Most possible variations are never made, and that is the design.
 *
 * What this module owes the rest of the system is a **stable order**. "The first n points"
 * has to mean the same thing in the Studio, in the model-sheet plan, and in a Cloud
 * Function, or a sheet's cells would not line up with the cells its split writes into.
 */
import { canonicalVariant } from './address.js';
import { AxesTooLargeError } from './errors.js';
import { canonicalJson } from './hash.js';
/**
 * The guardrail, as code. Axes multiply, so these are hard bounds rather than advice.
 *
 * They are a lean, not a settled product decision — exported so the Studio can read them
 * for its own copy instead of hard-coding a second, drifting set of numbers.
 */
export const MAX_AXES = 4;
export const MAX_OPTIONS_PER_AXIS = 8;
export const MAX_MATRIX_SIZE = 256;
/** How many points the axes describe. No axes is one point: the neutral variant. */
export function matrixSize(axes) {
    return axes.reduce((size, axis) => size * axis.options.length, 1);
}
/**
 * Refuse axes that describe a matrix too large to be honest about.
 *
 * Checked most-specific first, so the message names the thing to fix rather than only the
 * total it produced.
 */
export function assertAxesWithinBounds(axes) {
    if (axes.length > MAX_AXES) {
        throw new AxesTooLargeError('axes', axes.length, MAX_AXES, `a primitive may carry at most ${MAX_AXES} axes (got ${axes.length}) — axes multiply, so they are kept few and deliberate`);
    }
    for (const axis of axes) {
        if (axis.options.length > MAX_OPTIONS_PER_AXIS) {
            throw new AxesTooLargeError('options', axis.options.length, MAX_OPTIONS_PER_AXIS, `axis "${axis.key}" may carry at most ${MAX_OPTIONS_PER_AXIS} options (got ${axis.options.length})`);
        }
    }
    const size = matrixSize(axes);
    if (size > MAX_MATRIX_SIZE) {
        throw new AxesTooLargeError('size', size, MAX_MATRIX_SIZE, `these axes describe ${size} variants, past the ${MAX_MATRIX_SIZE} ceiling — remove an axis or some options`);
    }
}
/**
 * Every point in the matrix, in stable order: axes in the order given, options in the order
 * given, **the last axis varying fastest** — an odometer.
 *
 * No axes yields exactly one point, the neutral variant `{}`, which is the base reference.
 * Each point is a canonical variant, but the *ordering* is by axis order, not key order, so
 * renaming an axis never reshuffles a sheet.
 */
export function matrixPoints(axes) {
    let points = [{}];
    for (const axis of axes) {
        const next = [];
        for (const point of points) {
            for (const option of axis.options) {
                next.push({ ...point, [axis.key]: option.key });
            }
        }
        points = next;
    }
    return points.map(canonicalVariant);
}
/**
 * Is this a legal point? Every key must be an axis and every value one of its options.
 *
 * A **partial** variant is legal: leaving an axis out means "neutral on that axis", which is
 * how a two-axis character can still have a pose-only variant.
 */
export function isValidVariant(axes, variant) {
    for (const [key, value] of Object.entries(variant)) {
        const axis = axes.find((candidate) => candidate.key === key);
        if (axis === undefined)
            return false;
        if (!axis.options.some((option) => option.key === value))
            return false;
    }
    return true;
}
/**
 * The points one axis away — what the explorer offers as "make the next one".
 *
 * For a complete variant this is `Σ(options − 1)`. For a partial one, setting an axis that
 * was neutral is also a single step, so an unset axis contributes all of its options.
 */
export function neighbours(axes, variant) {
    const out = [];
    for (const axis of axes) {
        for (const option of axis.options) {
            if (variant[axis.key] === option.key)
                continue;
            out.push(canonicalVariant({ ...variant, [axis.key]: option.key }));
        }
    }
    return out;
}
/** The human label for one axis option, for a cell caption or an instruction clause. */
export function axisOptionLabel(axes, key, value) {
    return axes.find((axis) => axis.key === key)?.options.find((option) => option.key === value)
        ?.label;
}
/**
 * The explorer's whole grid in one call: every matrix point paired with the instance that
 * materializes it, or `null`.
 *
 * Matching is by canonical variant, so a stored instance found by key order rather than
 * address still lines up.
 */
export function materializedCells(axes, instances) {
    const byVariant = new Map();
    for (const instance of instances) {
        const key = canonicalJson(canonicalVariant(instance.variant));
        if (!byVariant.has(key))
            byVariant.set(key, instance);
    }
    return matrixPoints(axes).map((variant) => ({
        variant,
        instance: byVariant.get(canonicalJson(variant)) ?? null,
    }));
}
//# sourceMappingURL=matrix.js.map